/** @format */

import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SubscriptionToggleCards } from "./subscription-toggle-cards";
import { ModalPrompt } from "mydnv/components/generic-modal";
import { MyDnvDevAppSubscriptions } from "mydnv/components/development-application-subscriptions";
import { solid, regular } from "assets/icons";
import * as subscribeService from "mydnv/services/subscription-service";
import { Config } from "config";
import { useMyDNVContext } from "mydnv/context/mydnv-context";

const {
    faSquareExclamation,
    faBuildingColumns,
    faBullhorn,
    faNewspaper,
    faBookBlank,
    faBuilding,
    faSirenOn,
    faEnvelope,
} = solid;

export const SubscriptionPanel = () => {
    const [allSubscriptions, setAllSubscriptions] = useState([]);
    const [subscribedToList, setSubscribedToList] = useState([]);
    const [newsToggleState, setNewsToggleState] = useState(false);
    const [storiesToggleState, setStoriesToggleState] = useState(false);
    const [advisoriesToggleState, setAdvisoriesToggleState] = useState(false);
    const [councilDocumentsToggleState, setCouncilDocumentsToggleState] = useState(false);
    const [developmentApplicationsToggleState, setDevelopmentApplicationsToggleState] = useState(false);
    const [inputOpportunitiesToggleState, setInputOpportunitiesToggleState] = useState(false);
    const [selectedDevApplications, setSelectedDevApplications] = useState([]);

    const loginRequest = Config.MyDNV.loginRequest;
    let { instance, account } = useMyDNVContext();

    const runApiCall = useCallback(
        (callback, ...args) => {
            const request = {
                ...loginRequest,
                account: account,
            };

            instance
                .acquireTokenSilent(request)
                .then((response) => {
                    if (args.length > 0) {
                        callback(response.accessToken, ...args);
                    } else {
                        callback(response.accessToken);
                    }
                })
                .catch((e) => {
                    console.error(e);
                });
        },
        [account, instance, loginRequest],
    );

    const fetchData = useCallback(async (accessToken) => {
        fetchSubscriptions(accessToken);
        fetchSubscribedToList(accessToken);
    }, []);

    const fetchSubscriptions = async (accessToken) => {
        let response = await subscribeService.getSubscriptionTopics(accessToken);
        let subscriptions = response.data.map((subscription) => {
            if (subscription.name === "New applications") {
                return {
                    ...subscription,
                    name: "Significant development applications",
                };
            }

            return subscription;
        });

        setAllSubscriptions(subscriptions);
    };

    const fetchSubscribedToList = async (accessToken) => {
        let response = await subscribeService.getUserSubscribedTopics(accessToken);
        setSubscribedToList(response.data.map((entry) => ({ topic_Id: entry.topic_Id })));
    };

    const postSubscriptionsListChanges = async (accessToken, updatedList) => {
        let updateListIds = updatedList.map((list) => list.topic_Id);
        await subscribeService.updateSubscriptionsList(accessToken, updateListIds);
    };

    const saveSubscriptionsList = (newSubscriptionsList) => {
        runApiCall(postSubscriptionsListChanges, newSubscriptionsList);
        setSubscribedToList(newSubscriptionsList);
    };

    const setDevAppToggledStates = useCallback(
        (optionsList) => {
            let selectedOptions = [];
            optionsList.forEach((option) => {
                selectedOptions.push({
                    ...option,
                    Selected: subscribedToList.some((subscription) => subscription.topic_Id === option.id),
                });
            });

            // if there are no options selected
            if (selectedOptions.length === 0) {
                selectedOptions = optionsList.map((option) => ({
                    ...option,
                    Selected: true,
                }));
            }

            setSelectedDevApplications(selectedOptions);
        },
        [subscribedToList],
    );

    const topicIsSubscribedTo = useCallback(
        (subscription) => !!subscribedToList.find((subscribedTopic) => subscribedTopic.topic_Id === subscription.id),
        [subscribedToList],
    );

    const setAppropriateCardState = useCallback(
        (subscriptionName, subscriptionState) => {
            switch (subscriptionName) {
                case "news":
                    setNewsToggleState(subscriptionState);
                    break;

                case "stories":
                    setStoriesToggleState(subscriptionState);
                    break;

                case "advisories":
                    setAdvisoriesToggleState(subscriptionState);
                    break;

                case "council documents":
                    setCouncilDocumentsToggleState(subscriptionState);
                    break;

                case "input opportunities":
                    setInputOpportunitiesToggleState(subscriptionState);
                    break;

                case "significant development applications":
                    setDevelopmentApplicationsToggleState(subscriptionState);
                    if (!subscriptionState) {
                        setDevAppToggledStates([]);
                    }
                    break;

                default:
                    break;
            }
        },
        [setDevAppToggledStates],
    );

    const setInitialCardToggleStates = useCallback(
        (cardSubscriptions) => {
            cardSubscriptions.forEach((subscription) => {
                let isTopicSubscribedTo = topicIsSubscribedTo(subscription);
                setAppropriateCardState(subscription.name.toLowerCase(), isTopicSubscribedTo);
            });
        },
        [setAppropriateCardState, topicIsSubscribedTo],
    );

    const [modalToggleState, setModalToggleState] = useState(false);
    const openModal = () => setModalToggleState(true);
    const closeModal = () => setModalToggleState(false);

    const handleCardToggled = (cardName, updatedState) => {
        setAppropriateCardState(cardName.toLowerCase(), updatedState);

        let newSubList = [];
        let cardSubEntry = allSubscriptions.find((sub) => sub.name.toLowerCase() === cardName.toLowerCase());

        if (cardName === "Significant development applications") {
            // remove all subscribed development options
            if (!updatedState) {
                let nonDevEntryIds = allSubscriptions
                    .filter((sub) => sub.parent !== cardSubEntry.id && sub.id !== cardSubEntry.id)
                    .map((sub) => sub.id);

                newSubList = subscribedToList.filter((subToList) => nonDevEntryIds.includes(subToList.topic_Id));
            } else {
                let devEntries = allSubscriptions.filter((sub) => sub.parent === cardSubEntry.id);
                newSubList = [
                    ...subscribedToList,
                    { topic_Id: cardSubEntry.id },
                    ...devEntries.map((sub) => ({
                        topic_Id: sub.id,
                    })),
                ];
            }
        } else {
            newSubList = updatedState
                ? [...subscribedToList, { topic_Id: cardSubEntry.id }]
                : subscribedToList.filter((sub) => sub.topic_Id !== cardSubEntry.id);
        }

        saveSubscriptionsList(newSubList);
    };

    const handleDevOptionsSaveSelections = (selectionsList) => {
        let newSubList = [];

        // get the subscribedToList without the dev options applied to it
        let parentId = selectionsList[0].parent;
        let subscriptionsNonMatchingParent = allSubscriptions.filter((sub) => sub.parent !== parentId);

        if (!selectionsList.some((selection) => selection.Selected)) {
            let filteredSubscriptionIds = subscriptionsNonMatchingParent
                .filter((sub) => sub.id !== parentId)
                .map((sub) => sub.id);
            newSubList = subscribedToList.filter((subToList) => filteredSubscriptionIds.includes(subToList.topic_Id));
        } else {
            newSubList = [
                ...subscriptionsNonMatchingParent
                    .filter((sub) => subscribedToList.some((subscription) => subscription.topic_Id === sub.id))
                    .map((sub) => ({ topic_Id: sub.id })),
                ...selectionsList.filter((option) => option.Selected).map((sub) => ({ topic_Id: sub.id })),
            ];
        }

        saveSubscriptionsList(newSubList);
        closeModal();
    };

    useEffect(() => {
        if (allSubscriptions.length > 0 && subscribedToList.length > 0) {
            // set the states for the main toggles
            let cardSubscriptions = allSubscriptions.filter((subscription) => subscription.parent === null);
            setInitialCardToggleStates(cardSubscriptions);

            // grab the options list for the development applications
            let developmentTopic = allSubscriptions.find(
                (subscription) => subscription.name.toLowerCase() === "significant development applications",
            );
            if (developmentTopic) {
                let developmentTopicId = developmentTopic.id;
                let userIsDevelopmentSubscribed = subscribedToList.some(
                    (subscription) => subscription.topic_Id === developmentTopicId,
                );

                // set the selected development application options
                if (userIsDevelopmentSubscribed) {
                    let optionsList = allSubscriptions.filter(
                        (subscription) => subscription.parent === developmentTopicId,
                    );
                    setDevAppToggledStates(optionsList);
                }
            }
        }
    }, [allSubscriptions, setDevAppToggledStates, setInitialCardToggleStates, subscribedToList]);

    useEffect(() => {
        runApiCall(fetchData);
    }, []);

    const formatSubscribedDevApps = () => {
        const selectedDAs = selectedDevApplications.filter((item) => item.Selected);
        return selectedDAs.map((s) => s.name).join(", ");
    };

    const handleInnerClick = (e) => {
        e.stopPropagation();
    };

    return (
        <>
            <p>
                You&apos;ll receive email updates <FontAwesomeIcon icon={faEnvelope} /> for the content you subscribe
                to.
            </p>
            <div className="subscriptionToggleCards">
                <div className="smallToggleCards">
                    <div className="cardRow">
                        <SubscriptionToggleCards
                            cardTitle="Advisories"
                            cardDescription="Subscribe to advisories"
                            cardToggleState={advisoriesToggleState}
                            setToggleState={() => handleCardToggled("Advisories", !advisoriesToggleState)}
                            cardIcon={faSquareExclamation}
                            cardInfo={
                                "Stay informed about impacts to local services and facilities, including road and park closures, water advisories, and weather events."
                            }
                            cbId={"advisories"}
                        />
                        <div className="cardContainer border-on alerts">
                            <div className="justify-content-space-between subscribe-on">
                                <FontAwesomeIcon icon={faSirenOn} size="xl" />
                                <div className="alerts-circle">
                                    <FontAwesomeIcon icon={faEnvelope} transform="shrink-7" />
                                </div>
                            </div>
                            <div className="cardTitle">Alerts</div>
                            <div className="cardSwitchAndDesc">
                                <span className="cardInfo">
                                    By creating an account, you agreed to receive email alerts from the District in the
                                    event of a major emergency.
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="cardRow">
                        <SubscriptionToggleCards
                            cardTitle="Council documents"
                            cardDescription="Subscribe to Council documents"
                            cardToggleState={councilDocumentsToggleState}
                            setToggleState={() => handleCardToggled("Council documents", !councilDocumentsToggleState)}
                            cardIcon={faBuildingColumns}
                            cardInfo={
                                "Get notified when new Council meeting agendas, minutes, and reports are published, keeping you informed about municipal decisions."
                            }
                            cbId={"councilDocs"}
                        />

                        <SubscriptionToggleCards
                            cardTitle="Input opportunities"
                            cardDescription="Subscribe to input opportunities"
                            cardToggleState={inputOpportunitiesToggleState}
                            setToggleState={() =>
                                handleCardToggled("Input opportunities", !inputOpportunitiesToggleState)
                            }
                            cardIcon={faBullhorn}
                            cardInfo={
                                "Get notified when there are new opportunities to share your thoughts on important initiatives."
                            }
                            cbId={"inputOpps"}
                        />
                    </div>

                    <div className="cardRow">
                        <SubscriptionToggleCards
                            cardTitle="News"
                            cardDescription="Subscribe to news"
                            cardToggleState={newsToggleState}
                            setToggleState={() => handleCardToggled("News", !newsToggleState)}
                            cardIcon={faNewspaper}
                            cardInfo={
                                "Stay up-to-date with the latest news, community updates, and announcements from us."
                            }
                            cbId={"news"}
                        />

                        <SubscriptionToggleCards
                            cardTitle="Stories"
                            cardDescription="Subscribe to stories"
                            cardToggleState={storiesToggleState}
                            setToggleState={() => handleCardToggled("Stories", !storiesToggleState)}
                            cardIcon={faBookBlank}
                            cardInfo={
                                "Read about community highlights, and inspiring stories from around the District."
                            }
                            cbId={"stories"}
                        />
                    </div>
                </div>

                <div className="largeToggleCards">
                    <div className="cardRow">
                        <SubscriptionToggleCards
                            cardTitle="Significant development applications"
                            cardDescription="Subscribe to new significant development applications"
                            cardToggleState={developmentApplicationsToggleState}
                            setToggleState={() =>
                                handleCardToggled(
                                    "Significant development applications",
                                    !developmentApplicationsToggleState,
                                )
                            }
                            cardIcon={faBuilding}
                            cardInfo={
                                "Stay up-to-date on new significant development applications in your neighbourhood or across the District."
                            }
                            childComponent={
                                <>
                                    <div className="devApplicationSubscriptionsList" onClick={handleInnerClick}>
                                        <div className="selectedDevApplicationsOptionsList">
                                            {formatSubscribedDevApps()}
                                            <div className="openDevApplicationListModalBtn">
                                                <button onClick={openModal}>Edit areas</button>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            }
                            cbId={"devapps"}
                        />
                    </div>
                </div>
            </div>

            <ModalPrompt
                onModalClose={closeModal}
                modalToggleState={modalToggleState}
                modalTitle="Send me email updates about new significant development applications in:"
                modalContent={
                    <MyDnvDevAppSubscriptions
                        optionsList={selectedDevApplications}
                        onSave={handleDevOptionsSaveSelections}
                        onCancel={closeModal}
                    />
                }
            />
        </>
    );
};
